/**
 * useAppRouter - URL-based navigation hook for OmniTower.
 *
 * Syncs in-memory navigation state with the browser URL so that:
 * - Every domain page gets a clean pathname (/overview, /alerts, /dashboard/:id, etc.)
 * - Layer routes: /executive, /operations, /district, /store/:id, /floor/:id
 * - Browser back/forward buttons work
 * - Deep links and bookmarks are supported
 * - Refresh preserves current page
 *
 * State is source of truth; the URL is a mirror updated via pushState/replaceState.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { Page } from '../types';
import type { AdminSection } from '../components/admin/AdminLayout';

export type SettingsSection = 'company' | 'rules' | 'integrations' | 'data' | 'zones' | 'users' | 'health' | 'audit';

export interface AppRoute {
  page: Page;
  storeId?: string;
  /** Rule id for /live-ops/:ruleId */
  ruleId?: string;
  /** Tablero phase for /tablero/:phase deep-link */
  phase?: string;
  adminSection?: AdminSection;
  settingsSection?: SettingsSection;
  /** Preserved tab (e.g. Command Center active tab) */
  tab?: string;
}

// ── Mapping tables ──────────────────────────────────────────────

const PAGE_TO_PATH: Record<Page, string> = {
  overview: '/overview',
  dashboard: '/dashboard',
  alerts: '/alerts',
  cases: '/cases',
  tasks: '/tasks',
  reports: '/reports',
  admin: '/admin',
  'auditor-home': '/audit',
  'cashier-performance': '/cashiers',
  executive: '/executive',
  operations: '/operations',
  district: '/district',
  floor: '/floor',
  'command-center': '/command-center',
  forensics: '/forensics',
  settings: '/settings',
  'supply-chain': '/supply-chain',
  vsm: '/vsm',
  expediente: '/expediente',
  'live-ops': '/live-ops',
  mvp: '/mvp',
  'risk-tower': '/risk-tower',
  'inventory-overview': '/inicio',
  'tablero-operativo': '/tablero',
  glossary: '/diccionario',
  'constructor-kpis': '/constructor-kpis',
  'constructor-reglas': '/constructor-reglas',
  'risk-tower-pack': '/risk-tower-pack',
  diagnostico: '/diagnostico',
};

const PATH_TO_PAGE: Record<string, Page> = {
  overview: 'overview',
  dashboard: 'dashboard',
  alerts: 'alerts',
  cases: 'cases',
  tasks: 'tasks',
  reports: 'reports',
  admin: 'admin',
  audit: 'auditor-home',
  cashiers: 'cashier-performance',
  executive: 'executive',
  operations: 'operations',
  district: 'district',
  floor: 'floor',
  'command-center': 'command-center',
  forensics: 'forensics',
  settings: 'settings',
  'supply-chain': 'supply-chain',
  vsm: 'vsm',
  expediente: 'expediente',
  'live-ops': 'live-ops',
  mvp: 'mvp',
  'risk-tower': 'risk-tower',
  inicio: 'inventory-overview',
  tablero: 'tablero-operativo',
  diccionario: 'glossary',
  'constructor-kpis': 'constructor-kpis',
  'constructor-reglas': 'constructor-reglas',
  'risk-tower-pack': 'risk-tower-pack',
  diagnostico: 'diagnostico',
};

const ADMIN_SECTION_MAP: Record<string, AdminSection> = {
  company: 'company',
  plan: 'plan',
  rules: 'rules',
  data: 'data',
  zones: 'zones',
  users: 'users',
  health: 'health',
};

const SETTINGS_SECTION_MAP: Record<string, SettingsSection> = {
  company: 'company',
  rules: 'rules',
  integrations: 'integrations',
  data: 'data',
  zones: 'zones',
  users: 'users',
  health: 'health',
  audit: 'audit',
};

// Pages that accept a /:storeId parameter
const STORE_PARAM_PAGES = new Set<string>(['dashboard', 'store', 'floor']);

// ── Parse / Build ───────────────────────────────────────────────

export function parseRoute(pathname: string, search?: string): AppRoute {
  // Strip trailing slash (except root)
  const clean = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
  const segments = clean.split('/').filter(Boolean); // e.g. ['dashboard','abc-123']
  const params = new URLSearchParams(search ?? (typeof window !== 'undefined' ? window.location.search : ''));
  const tab = params.get('tab') ?? undefined;

  if (segments.length === 0) {
    return { page: 'risk-tower' };
  }

  const first = segments[0];

  // /settings or /settings/:section
  if (first === 'settings') {
    const sectionSlug = segments[1];
    const settingsSection: SettingsSection | undefined =
      sectionSlug && SETTINGS_SECTION_MAP[sectionSlug]
        ? SETTINGS_SECTION_MAP[sectionSlug]
        : undefined;
    return { page: 'settings', settingsSection };
  }

  // /admin or /admin/:section
  if (first === 'admin') {
    const sectionSlug = segments[1];
    const adminSection: AdminSection =
      sectionSlug && ADMIN_SECTION_MAP[sectionSlug]
        ? ADMIN_SECTION_MAP[sectionSlug]
        : 'company';
    return { page: 'admin', adminSection };
  }

  // /dashboard or /dashboard/:storeId
  if (first === 'dashboard') {
    const storeId = segments[1];
    return storeId
      ? { page: 'dashboard', storeId }
      : { page: 'dashboard' };
  }

  // /store/:storeId — Layer 4 store view (maps to 'dashboard' page with storeId)
  if (first === 'store') {
    const storeId = segments[1];
    return storeId
      ? { page: 'dashboard', storeId }
      : { page: 'dashboard' };
  }

  // /floor/:storeId — Layer 5 floor staff view
  if (first === 'floor') {
    const storeId = segments[1];
    return storeId
      ? { page: 'floor', storeId }
      : { page: 'floor' };
  }

  // /live-ops/:ruleId — Level 3 realtime rule view (PR #11)
  if (first === 'live-ops') {
    const ruleId = segments[1];
    return ruleId
      ? { page: 'live-ops', ruleId }
      : { page: 'live-ops' };
  }

  // /tablero or /tablero/:phase
  if (first === 'tablero') {
    const phase = segments[1];
    return phase ? { page: 'tablero-operativo', phase } : { page: 'tablero-operativo' };
  }

  // All other known pages
  const page = PATH_TO_PAGE[first];
  if (page) {
    const result: AppRoute = { page };
    if (page === 'command-center' && tab) result.tab = tab;
    return result;
  }

  // Unknown path — fall back to risk-tower (default home)
  return { page: 'risk-tower' };
}

export function buildPath(route: AppRoute): string {
  let path: string;
  if (route.page === 'dashboard' && route.storeId) {
    path = `/dashboard/${route.storeId}`;
  } else if (route.page === 'floor' && route.storeId) {
    path = `/floor/${route.storeId}`;
  } else if (route.page === 'live-ops' && route.ruleId) {
    path = `/live-ops/${route.ruleId}`;
  } else if (route.page === 'settings') {
    path = route.settingsSection ? `/settings/${route.settingsSection}` : '/settings';
  } else if (route.page === 'admin') {
    const section = route.adminSection ?? 'company';
    path = section === 'company' ? '/admin' : `/admin/${section}`;
  } else if (route.page === 'tablero-operativo' && route.phase) {
    path = `/tablero/${route.phase}`;
  } else {
    path = PAGE_TO_PATH[route.page] ?? '/overview';
  }
  // Append ?tab= for Command Center tab persistence
  if (route.tab) {
    path += `?tab=${encodeURIComponent(route.tab)}`;
  }
  return path;
}

// ── Helpers ─────────────────────────────────────────────────────

/** Build a full URL string from a route, preserving unmanaged query params (e.g. ?layout=compact). */
function buildFullUrl(route: AppRoute): string {
  const target = buildPath(route);
  const targetUrl = new URL(target, window.location.origin);
  const currentParams = new URLSearchParams(window.location.search);
  const managedParams = new Set(['tab']);
  currentParams.forEach((value, key) => {
    if (!managedParams.has(key) && !targetUrl.searchParams.has(key)) {
      targetUrl.searchParams.set(key, value);
    }
  });
  return targetUrl.pathname + targetUrl.search;
}

// ── Hook ────────────────────────────────────────────────────────

export function useAppRouter() {
  const [route, setRoute] = useState<AppRoute>(() => parseRoute(window.location.pathname, window.location.search));
  const isPopRef = useRef(false);

  // On mount: if the user landed on "/" replace it with the path of the page "/"
  // resolves to (parseRoute → risk-tower), so the URL matches what is rendered and
  // Back doesn't land on a different page.
  useEffect(() => {
    if (window.location.pathname === '/' || window.location.pathname === '') {
      const qs = window.location.search;
      window.history.replaceState(null, '', buildPath(parseRoute('/', qs)) + qs);
    }
  }, []);

  // Listen for browser back/forward
  useEffect(() => {
    const onPop = () => {
      isPopRef.current = true;
      setRoute(parseRoute(window.location.pathname, window.location.search));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Mirror state → URL (skip when triggered by popstate to avoid loops)
  // Preserves query params not managed by the router (e.g., ?layout=compact for A/B tests)
  useEffect(() => {
    if (isPopRef.current) {
      isPopRef.current = false;
      return;
    }
    const fullTarget = buildFullUrl(route);
    const currentUrl = window.location.pathname + window.location.search;
    if (currentUrl !== fullTarget) {
      window.history.pushState(null, '', fullTarget);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.page, route.storeId, route.ruleId, route.phase, route.adminSection, route.settingsSection, route.tab]);

  // Primary navigation (creates history entry)
  const navigate = useCallback(
    (page: Page, opts?: { storeId?: string; ruleId?: string; phase?: string; adminSection?: AdminSection; settingsSection?: SettingsSection; tab?: string }) => {
      setRoute({
        page,
        storeId: opts?.storeId,
        ruleId: page === 'live-ops' ? opts?.ruleId : undefined,
        phase: page === 'tablero-operativo' ? opts?.phase : undefined,
        adminSection: page === 'admin' ? (opts?.adminSection ?? 'company') : undefined,
        settingsSection: page === 'settings' ? opts?.settingsSection : undefined,
        tab: page === 'command-center' ? opts?.tab : undefined,
      });
    },
    [],
  );

  // Admin sub-section change (replaceState — no extra history entries)
  const setAdminSection = useCallback((section: AdminSection) => {
    setRoute(prev => {
      const next = { ...prev, adminSection: section };
      window.history.replaceState(null, '', buildFullUrl(next));
      isPopRef.current = true;
      return next;
    });
  }, []);

  // Settings sub-section change (replaceState — no extra history entries)
  const setSettingsSection = useCallback((section: SettingsSection | undefined) => {
    setRoute(prev => {
      const next: AppRoute = { ...prev, page: 'settings' as Page, settingsSection: section };
      window.history.replaceState(null, '', buildFullUrl(next));
      isPopRef.current = true;
      return next;
    });
  }, []);

  // Command Center tab change (replaceState — no extra history entries)
  const setTab = useCallback((tab: string | undefined) => {
    setRoute(prev => {
      const next: AppRoute = { ...prev, tab };
      window.history.replaceState(null, '', buildFullUrl(next));
      isPopRef.current = true;
      return next;
    });
  }, []);

  return { route, navigate, setAdminSection, setSettingsSection, setTab } as const;
}
